import type { CrowdexUIMessage } from '@crowdex/agent-contracts';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@crowdex/ui/components/dialog';
import { cn } from '@crowdex/ui/lib/utils';
import {
  Brain,
  Check,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  CircleStop,
  FolderOpen,
  Hourglass,
  LoaderCircle,
  Pencil,
  Search,
  SquareTerminal,
  X,
} from 'lucide-react';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { SHEET_ON_PHONE } from '@/components/desktop-shell/dialog-sheet';
import { useMediaQuery } from '@/hooks/use-media-query';
import { type StepRow, stepRows } from '@/lib/conversations/message-part/step-rows';
import {
  formatDuration,
  stepsLabel,
  type TurnActivity,
  type TurnLayout,
  turnActivity,
  turnTimes,
} from '@/lib/conversations/message-part/turn-steps';
import { DESKTOP_LAYOUT_QUERY } from '@/lib/desktop-shell/desktop-layout';
import { CodeStepRow } from './code-step';
import { DynamicTool } from './dynamic-tool';
import { ElementSelectionText } from './element-selection-text';

/** How long a running turn has worked, read again every few seconds; null when unknown. */
function useTurnDuration(message: CrowdexUIMessage, running: boolean): number | null {
  const times = turnTimes(message);
  const start = times?.start ?? null;
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!running || start === null) return;
    setNow(Date.now());
    const timer = window.setInterval(() => setNow(Date.now()), 15_000);
    return () => window.clearInterval(timer);
  }, [running, start]);
  if (times === null) return null;
  if (times.end !== null) return times.end - times.start;
  return running ? now - times.start : null;
}

function RowLine({
  icon,
  title,
  detail,
  mono = false,
  onClick,
  expanded,
}: {
  icon: ReactNode;
  title: ReactNode;
  detail?: ReactNode;
  mono?: boolean;
  onClick?: (() => void) | undefined;
  /** Of a row that opens in place, whether it is open. */
  expanded?: boolean | undefined;
}) {
  const content = (
    <>
      <span className="flex size-4 shrink-0 items-center justify-center text-muted-foreground">
        {icon}
      </span>
      <span
        className={cn(
          'min-w-0 flex-1 truncate text-foreground',
          mono ? 'type-meta font-mono' : 'text-sm',
        )}
      >
        {title}
      </span>
      {detail !== undefined && (
        <span className="type-meta max-w-[45%] shrink-0 truncate text-muted-foreground">
          {detail}
        </span>
      )}
    </>
  );
  return onClick === undefined ? (
    <div className="flex min-h-8 items-center gap-3">{content}</div>
  ) : (
    <button
      aria-expanded={expanded}
      className="flex min-h-8 w-full items-center gap-3 text-left hover:opacity-80"
      onClick={onClick}
      type="button"
    >
      {content}
    </button>
  );
}

/**
 * A thought: its first line, which opens on its whole text (read as it streams while it runs).
 */
function ThoughtRow({ row }: { row: Extract<StepRow, { kind: 'thought' }> }) {
  const [open, setOpen] = useState(false);
  const firstLine = row.text.split('\n')[0] ?? '';
  return (
    <div>
      <RowLine
        detail={
          row.text === '' ? undefined : open ? (
            <ChevronDown className="size-3.5" />
          ) : (
            <ChevronRight className="size-3.5" />
          )
        }
        expanded={row.text === '' ? undefined : open}
        icon={<Brain className={cn('size-3.5', row.running && 'text-running')} />}
        onClick={row.text === '' ? undefined : () => setOpen((value) => !value)}
        title={
          row.running
            ? 'Thinking'
            : row.durationMs === null
              ? 'Thought'
              : `Thought for ${formatDuration(row.durationMs)}`
        }
      />
      {row.text !== '' &&
        (open ? (
          <p
            className="type-meta -mt-1 mb-1 ml-7 whitespace-pre-wrap break-words text-muted-foreground italic"
            data-thought-text
          >
            {row.text}
          </p>
        ) : (
          <p className="type-meta -mt-1 mb-1 ml-7 truncate text-muted-foreground italic">
            {firstLine}
          </p>
        ))}
    </div>
  );
}

/** One step, as its row (design D12). */
function StepRowView({
  row,
  onOpenTerminal,
  onReviewFile,
}: {
  row: StepRow;
  onOpenTerminal?: ((terminalId: string) => void) | undefined;
  onReviewFile?: ((path: string) => void) | undefined;
}) {
  switch (row.kind) {
    case 'reads':
      return (
        <RowLine
          detail={
            row.paths.length > 1
              ? `${row.paths.slice(0, 3).join(', ')}${row.paths.length > 3 ? '…' : ''}`
              : undefined
          }
          icon={<FolderOpen className="size-3.5" />}
          title={row.paths.length === 1 ? `Read ${row.paths[0]}` : `Read ${row.paths.length} files`}
        />
      );
    case 'search':
      return (
        <RowLine
          detail={
            row.results === null
              ? undefined
              : `${row.results} ${row.results === 1 ? 'result' : 'results'}`
          }
          icon={<Search className="size-3.5" />}
          title={row.title}
        />
      );
    case 'edit':
      return (
        <div>
          <RowLine
            detail={
              <span className="font-mono">
                {row.additions > 0 && <span className="text-success">+{row.additions}</span>}
                {row.additions > 0 && row.deletions > 0 && ' '}
                {row.deletions > 0 && <span className="text-destructive">−{row.deletions}</span>}
              </span>
            }
            icon={<Pencil className="size-3.5" />}
            title={`Edited ${row.path}`}
          />
          {row.excerpt.length > 0 && (
            <pre className="mt-1 mb-1.5 ml-7 overflow-hidden rounded-xl bg-code-surface px-3 py-2 font-mono text-xs leading-5">
              {row.excerpt.map((line, index) => (
                <span
                  className={cn(
                    'block truncate',
                    line.sign === '+' ? 'text-success' : 'text-destructive',
                  )}
                  // biome-ignore lint/suspicious/noArrayIndexKey: The excerpt's lines may repeat; their order is their identity.
                  key={index}
                >
                  {line.sign} {line.text.trim()}
                </span>
              ))}
            </pre>
          )}
          {onReviewFile !== undefined && (
            <button
              className="type-meta mb-1 ml-7 text-running hover:underline"
              onClick={() => onReviewFile(row.fullPath)}
              type="button"
            >
              Open in Review
            </button>
          )}
        </div>
      );
    case 'command':
      return (
        <RowLine
          detail={
            <span className={row.state === 'failed' ? 'text-destructive' : undefined}>
              {row.detail}
            </span>
          }
          icon={
            // The turn's activity line is its one spinner: a running step is said in colour.
            row.state === 'running' ? (
              <SquareTerminal className="size-3.5 text-running" />
            ) : row.state === 'failed' ? (
              <X className="size-3.5 text-destructive" />
            ) : (
              <SquareTerminal className="size-3.5" />
            )
          }
          mono
          onClick={
            row.terminalId === null || onOpenTerminal === undefined
              ? undefined
              : () => onOpenTerminal(row.terminalId as string)
          }
          title={row.command}
        />
      );
    case 'thought':
      return <ThoughtRow row={row} />;
    case 'text':
      return (
        <div className="type-meta py-1 text-muted-foreground">
          <ElementSelectionText text={row.text} />
        </div>
      );
    case 'code':
      return <CodeStepRow images={row.images} step={row.step} />;
    case 'tool':
      return (
        <DynamicTool
          onOpenTerminal={onOpenTerminal}
          part={row.part}
          {...(row.terminal === undefined ? {} : { terminal: row.terminal })}
        />
      );
  }
}

/**
 * The turn's activity line, closed (design D12; boards DeskSteps, DeskApproval): one pill, the
 * same in every state, so a turn never shows two kinds of activity. Only a working turn spins.
 */
function activityPillClass(phone: boolean) {
  return cn(
    'type-meta inline-flex max-w-full items-center gap-2 self-start rounded-control border border-hairline bg-surface-raised px-3 text-muted-foreground',
    // A finger's height on a phone (M17).
    phone ? 'h-11' : 'h-8',
  );
}

function ActivityIcon({ activity }: { activity: TurnActivity }) {
  const className = 'size-3.5 shrink-0';
  switch (activity) {
    case 'working':
      return (
        <LoaderCircle
          aria-hidden
          className={cn(className, 'animate-spin text-running')}
          data-turn-spinner
        />
      );
    case 'waiting':
      return <Hourglass aria-hidden className={cn(className, 'text-attention')} />;
    case 'stopped':
      return <CircleStop aria-hidden className={className} />;
    case 'done':
      return <Check aria-hidden className={cn(className, 'text-success')} />;
  }
}

/**
 * A turn working before its first step (or a message sent, not answered yet): the activity line
 * alone, "Working", that its steps' count joins once it has some.
 */
export function TurnWorking({ label = 'Working' }: { label?: string }) {
  const phone = !useMediaQuery(DESKTOP_LAYOUT_QUERY);
  return (
    <div
      aria-live="polite"
      className={activityPillClass(phone)}
      data-turn-steps="working"
      role="status"
    >
      <ActivityIcon activity="working" />
      <span className="truncate">{label}</span>
    </div>
  );
}

/**
 * A turn's steps folded into one line (design D12): "Worked 6 min · 9 steps", which opens in
 * place on a desktop (one row per step, what they did on the right), and in a sheet on a phone
 * (mobile 10). It opens by itself when a step waits for the user. It is the turn's one activity
 * line: "Working 3 min · 5 steps" with the turn's only spinner while it works, "Waits on you" in
 * amber while a tool asks the user, and "Working" alone before the first step.
 */
export function TurnSteps({
  layout,
  message,
  running,
  waiting = false,
  open,
  onOpenChange,
  onOpenTerminal,
  onReviewFile,
  final = true,
}: {
  layout: TurnLayout;
  message: CrowdexUIMessage;
  /**
   * The turn's last run of steps (the default), whose line says where the turn stands: its time,
   * stopped. An earlier run, between the turn's words, says only what it did.
   */
  final?: boolean;
  /** The turn is still streaming. */
  running: boolean;
  /** A tool of the turn asks the user (an approval, a question). */
  waiting?: boolean;
  onOpenTerminal?: ((terminalId: string) => void) | undefined;
  /** Opens an edited file's change of this turn in the Review tab. */
  onReviewFile?: ((path: string) => void) | undefined;
  /** Whether the steps are open: the turn shows its test runs as cards only when they are not. */
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const phone = !useMediaQuery(DESKTOP_LAYOUT_QUERY);
  // Still streaming, or a step still runs (a tool waiting on its result or on the user).
  const active = running || layout.running;
  const activity = turnActivity({
    active,
    waiting,
    stopped: final && message.metadata?.stopReason === 'cancelled',
  });
  const turnDuration = useTurnDuration(message, active);
  const duration = final ? turnDuration : null;
  // Opened or closed from its button, the steps' other button takes the focus: on a desktop the
  // pill and the open list's heading are two buttons, one replacing the other (M5).
  const toggleRef = useRef<HTMLButtonElement>(null);
  const refocus = useRef(false);
  // biome-ignore lint/correctness/useExhaustiveDependencies(open): runs once the steps opened or closed, when toggleRef holds the button that replaced the one clicked.
  useEffect(() => {
    if (!refocus.current) return;
    refocus.current = false;
    toggleRef.current?.focus();
  }, [open]);
  const setOpen = (next: boolean) => {
    refocus.current = !phone;
    onOpenChange(next);
  };
  const label = stepsLabel({ activity, count: layout.count, duration });
  if (layout.count === 0 && layout.steps.length === 0) {
    // Before its first step, the turn says it works until it shows something (its words, a card).
    const showsSomething = layout.shown.some(
      (item) =>
        item.kind !== 'standalone' ||
        (item.part.type === 'text'
          ? item.part.text.trim() !== ''
          : item.part.type !== 'step-start'),
    );
    return activity === 'working' && !showsSomething ? <TurnWorking label={label} /> : null;
  }
  const labelText = (
    <span className={cn('truncate', activity === 'waiting' && 'text-attention')}>{label}</span>
  );
  const stepList = stepRows(layout.parts, message.parts);
  const rows = (
    <ol className="divide-y divide-hairline" data-turn-steps-list>
      {stepList.map((row) => (
        <li className="px-3.5 py-1" key={row.key}>
          <StepRowView onOpenTerminal={onOpenTerminal} onReviewFile={onReviewFile} row={row} />
        </li>
      ))}
    </ol>
  );

  if (!phone && open) {
    return (
      <section
        aria-label="Steps"
        className="overflow-hidden rounded-card border border-hairline bg-card"
        data-turn-activity={activity}
        data-turn-steps="open"
      >
        <button
          aria-expanded
          ref={toggleRef}
          className="type-meta flex h-9 w-full items-center gap-2 border-hairline border-b px-3.5 text-left text-muted-foreground hover:text-foreground"
          onClick={() => setOpen(false)}
          type="button"
        >
          <ActivityIcon activity={activity} />
          {labelText}
          <span className="ml-auto truncate">{layout.summary}</span>
          <ChevronUp aria-hidden className="size-3.5 shrink-0" />
        </button>
        {rows}
      </section>
    );
  }

  return (
    <>
      <button
        aria-expanded={open}
        ref={toggleRef}
        className={cn(activityPillClass(phone), 'transition-colors hover:text-foreground')}
        data-turn-activity={activity}
        data-turn-steps="closed"
        onClick={() => setOpen(true)}
        type="button"
      >
        <ActivityIcon activity={activity} />
        {labelText}
        {phone ? (
          <ChevronRight aria-hidden className="size-3.5 shrink-0" />
        ) : (
          <ChevronDown aria-hidden className="size-3.5 shrink-0" />
        )}
      </button>
      {phone && (
        <Dialog onOpenChange={setOpen} open={open}>
          <DialogContent
            className={cn('gap-2 px-0 pt-5 pb-3', SHEET_ON_PHONE)}
            data-turn-steps="sheet"
          >
            <div className="px-5 text-center">
              <DialogTitle className="type-card-title">
                {label.split(' · ')[0] ?? label}
              </DialogTitle>
              <DialogDescription className="type-meta mt-0.5 text-muted-foreground">
                {[`${layout.count} ${layout.count === 1 ? 'step' : 'steps'}`, layout.summary]
                  .filter(Boolean)
                  .join(' · ')}
              </DialogDescription>
            </div>
            <div className="max-h-[70dvh] overflow-y-auto">{rows}</div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
